import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ManualEntryForm = ({ credits, onCreditsChange }) => {
  const [selectedRole, setSelectedRole] = useState('director');

  const roleOptions = [
    { value: 'director', label: 'Director' },
    { value: 'producer', label: 'Producer' },
    { value: 'executive_producer', label: 'Executive Producer' },
    { value: 'writer', label: 'Writer' },
    { value: 'cinematographer', label: 'Cinematographer' },
    { value: 'editor', label: 'Editor' },
    { value: 'composer', label: 'Music Composer' },
    { value: 'actor', label: 'Actor' },
    { value: 'production_designer', label: 'Production Designer' },
    { value: 'costume_designer', label: 'Costume Designer' },
    { value: 'sound_designer', label: 'Sound Designer' },
    { value: 'visual_effects', label: 'Visual Effects' },
    { value: 'other', label: 'Other' }
  ];

  const addCredit = () => {
    const newCredit = {
      id: Date.now()?.toString(),
      role: selectedRole,
      name: '',
      character: selectedRole === 'actor' ? '' : null,
      order: credits?.length
    };
    onCreditsChange([...credits, newCredit]);
  };

  const updateCredit = (id, field, value) => {
    const updatedCredits = credits?.map(credit =>
      credit?.id === id ? { ...credit, [field]: value } : credit
    );
    onCreditsChange(updatedCredits);
  };

  const removeCredit = (id) => {
    const updatedCredits = credits?.filter(credit => credit?.id !== id);
    onCreditsChange(updatedCredits);
  };

  const handleDragEnd = (result) => {
    if (!result?.destination) return;

    const items = Array.from(credits);
    const [reorderedItem] = items?.splice(result?.source?.index, 1);
    items?.splice(result?.destination?.index, 0, reorderedItem);

    const reorderedCredits = items?.map((item, index) => ({
      ...item,
      order: index
    }));

    onCreditsChange(reorderedCredits);
  };

  const getRoleLabel = (roleValue) => {
    const role = roleOptions?.find(r => r?.value === roleValue);
    return role ? role?.label : roleValue;
  };

  return (
    <div className="space-y-6">
      {/* Add Credit Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Add New Credit</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Select
              label="Role"
              options={roleOptions}
              value={selectedRole}
              onChange={setSelectedRole}
              placeholder="Select role"
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={addCredit}
            >
              Add Credit
            </Button>
          </div>
        </div>
      </div>
      {/* Credits List */}
      {credits?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Credits ({credits?.length})</h3>
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Icon name="GripVertical" size={16} />
              Drag to reorder
            </div>
          </div>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="credits">
              {(provided) => (
                <div {...provided?.droppableProps} ref={provided?.innerRef} className="space-y-3">
                  {credits?.map((credit, index) => (
                    <Draggable key={credit?.id} draggableId={credit?.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided?.innerRef}
                          {...provided?.draggableProps}
                          className={`bg-muted border border-border rounded-lg p-4 transition-all ${
                            snapshot?.isDragging ? 'shadow-elevation-2 rotate-1' : ''
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div
                              {...provided?.dragHandleProps}
                              className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing mt-2"
                            >
                              <Icon name="GripVertical" size={16} />
                            </div>

                            <div className="flex-1 space-y-3">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded">
                                  {getRoleLabel(credit?.role)}
                                </span>
                                <span className="text-xs text-muted-foreground">#{index + 1}</span>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Input
                                  label="Name"
                                  type="text"
                                  placeholder="Enter name"
                                  value={credit?.name}
                                  onChange={(e) => updateCredit(credit?.id, 'name', e?.target?.value)}
                                  required
                                />

                                {credit?.character !== null && (
                                  <Input
                                    label="Character"
                                    type="text"
                                    placeholder="Character name"
                                    value={credit?.character || ''}
                                    onChange={(e) => updateCredit(credit?.id, 'character', e?.target?.value)}
                                  />
                                )}
                              </div>
                            </div>

                            <Button
                              variant="ghost"
                              size="icon"
                              iconName="Trash2"
                              onClick={() => removeCredit(credit?.id)}
                              className="text-error hover:text-error hover:bg-error/10 mt-2"
                            />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided?.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}
      {credits?.length === 0 && (
        <div className="bg-muted/50 border-2 border-dashed border-border rounded-lg p-12 text-center">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Credits Added</h3>
          <p className="text-muted-foreground mb-4">
            Start building your film credits by adding roles and names above.
          </p>
        </div>
      )}
    </div>
  );
};

export default ManualEntryForm;